import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Plus, Search, User, Phone, Mail, MapPin, ChevronRight, Car, MessageCircle, EyeOff, Eye, Send, Upload, Download, Trash2, CheckSquare, Square, AlertTriangle, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ClientsPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const isAdmin = auth?.admin?.role === 'admin' || auth?.admin?.is_admin === true;
  const emptyForm = {
    client_type: 'particulier', company_name: '',
    contact_first_name: '', contact_last_name: '', contact_phone: '', contact_email: '',
    salutation: '', first_name: '', last_name: '', phone: '', email: '', address: '', npa: '', city: '',
    birthday: '', notification_preference: 'whatsapp', notes: '',
    status: 'prospect', current_vehicle_brand: '', current_vehicle_model: '',
    accepts_offers: false, offer_channel: 'none', next_follow_up_date: '',
    source: '', was_previous_client: false, do_not_contact: false, no_longer_owns_vehicle: false,
  };
  const [form, setForm] = useState(emptyForm);
  const [showInactive, setShowInactive] = useState(false);
  const [step, setStep] = useState('client'); // 'client' or 'vehicle'
  const [newClientId, setNewClientId] = useState(null);
  const [vForm, setVForm] = useState({ brand: '', model: '', type: '', plate: '', mileage: 0, vin: '' });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromAgenda = searchParams.get('from') === 'agenda';
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [filterCivil, setFilterCivil] = useState('all');
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const replaceFileRef = useRef(null);

  // Load stats on mount (instant, lightweight)
  useEffect(() => {
    axios.get(`${API}/clients/stats`).then(r => setStats(r.data)).catch(() => {});
  }, []);

  // Load clients only when searching or filter active
  useEffect(() => {
    if (search || filterType !== 'all' || filterCivil !== 'all') {
      loadClients();
    } else {
      setClients([]);
      setLoading(false);
    }
  }, [search, showInactive, filterType, filterCivil]);

  // Auto-open new client dialog when coming from agenda
  useEffect(() => {
    if (fromAgenda) { openNew(); }
  }, [fromAgenda]);

  // Compute filtered list for reuse
  const filteredClients = useMemo(() => {
    let filtered = clients;
    if (filterType !== 'all') filtered = filtered.filter(c => c.client_type === filterType);
    if (filterCivil !== 'all') filtered = filtered.filter(c => c.salutation === filterCivil);
    return filtered;
  }, [clients, filterType, filterCivil]);

  const loadClients = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (showInactive) params.show_inactive = true;
      const res = await axios.get(`${API}/clients`, { params });
      setClients(res.data);
      setSelectedClients(new Set());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const refreshStats = () => {
    axios.get(`${API}/clients/stats`).then(r => setStats(r.data)).catch(() => {});
  };

  const toggleSelectClient = (clientId, e) => {
    e.stopPropagation();
    setSelectedClients(prev => {
      const next = new Set(prev);
      if (next.has(clientId)) next.delete(clientId);
      else next.add(clientId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedClients.size === filteredClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(filteredClients.map(c => c.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.size === 0) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/clients/bulk-delete`, { client_ids: [...selectedClients] });
      toast.success(res.data.message);
      setShowDeleteConfirm(false);
      setSelectedClients(new Set());
      refreshStats();
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const toggleClientActive = async (e, clientId) => {
    e.stopPropagation();
    try {
      const res = await axios.patch(`${API}/clients/${clientId}/toggle-active`);
      toast.success(res.data.message);
      loadClients();
    } catch (err) { toast.error('Erreur'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.client_type === 'entreprise') {
      if (!form.company_name) { toast.error('Raison sociale requise'); return; }
    } else {
      if (!form.first_name || !form.last_name) { toast.error('Nom et prenom requis'); return; }
    }
    const payload = {
      ...form,
      first_name: form.client_type === 'entreprise' ? (form.company_name || '') : form.first_name,
      last_name: form.client_type === 'entreprise' ? '' : form.last_name,
      address: form.address,
      postal_code: form.npa,
      city: form.city,
      created_by: auth?.admin?.id || '',
      created_by_name: auth?.admin?.first_name ? `${auth.admin.first_name} ${auth.admin.last_name || ''}`.trim() : '',
      assigned_to: form.assigned_to || auth?.admin?.id || '',
      assigned_to_name: form.assigned_to_name || (auth?.admin?.first_name ? `${auth.admin.first_name} ${auth.admin.last_name || ''}`.trim() : ''),
    };
    try {
      if (editClient) {
        await axios.put(`${API}/clients/${editClient.id}`, payload);
        toast.success('Client modifié');
        setShowDialog(false);
        setEditClient(null);
        setForm(emptyForm);
        refreshStats();
        loadClients();
      } else {
        const res = await axios.post(`${API}/clients`, payload);
        toast.success('Client créé');
        setNewClientId(res.data.id);
        setStep('vehicle');
      }
    } catch (err) { toast.error('Erreur'); }
  };

  const handleVehicleSubmit = async (e) => {
    e.preventDefault();
    if (!vForm.brand || !vForm.plate) { toast.error('Marque et plaque requises'); return; }
    try {
      await axios.post(`${API}/vehicles`, { ...vForm, client_id: newClientId });
      toast.success('Véhicule ajouté');
      setShowDialog(false);
      setStep('client');
      setForm({ first_name: '', last_name: '', phone: '', email: '', address: '', npa: '', city: '', notification_preference: 'whatsapp', notes: '' });
      setVForm({ brand: '', model: '', type: '', plate: '', mileage: 0, vin: '' });
      loadClients();
      if (fromAgenda) {
        navigate(`/admin/agenda?newClientId=${newClientId}`);
      } else {
        navigate(`/admin/clients/${newClientId}`);
      }
      setNewClientId(null);
    } catch (err) { toast.error('Erreur'); }
  };

  const skipVehicle = () => {
    setShowDialog(false);
    setStep('client');
    if (fromAgenda) {
      navigate(`/admin/agenda?newClientId=${newClientId}`);
    } else {
      navigate(`/admin/clients/${newClientId}`);
    }
    setNewClientId(null);
  };

  const handleExport = async (contactOnly = false) => {
    try {
      const url = contactOnly ? `${API}/clients/export?contact_only=true` : `${API}/clients/export`;
      const res = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = contactOnly ? `clients_vortekio_avec_contact.xlsx` : `clients_vortekio.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(contactOnly ? 'Export (avec contact) telecharge' : 'Export telecharge');
    } catch (err) {
      toast.error("Erreur lors de l'export");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/clients/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { imported, skipped, errors } = res.data;
      toast.success(`${imported} client(s) importe(s)${skipped ? `, ${skipped} ignore(s)` : ''}`);
      if (errors?.length) toast.warning(`${errors.length} erreur(s) lors de l'import`);
      refreshStats();
      loadClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setImporting(false);
      if (importFileRef.current) importFileRef.current.value = '';
    }
  };

  const handleReplaceAll = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/clients/replace-all`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const { deleted, imported, errors } = res.data;
      toast.success(`${deleted} client(s) supprime(s), ${imported} client(s) importe(s)`);
      if (errors?.length) toast.warning(`${errors.length} erreur(s) lors de l'import`);
      setShowReplaceConfirm(false);
      refreshStats();
      setClients([]);
      setSearch('');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du remplacement");
    } finally {
      setReplacing(false);
      if (replaceFileRef.current) replaceFileRef.current.value = '';
    }
  };

  const openEdit = async (client) => {
    setEditClient(client);
    setStep('client');
    setShowDialog(true);
    // Fetch full client data (list view doesn't include all fields)
    try {
      const res = await axios.get(`${API}/clients/${client.id}`);
      const c = res.data;
      let street = c.address || '';
      let npa = c.postal_code || '';
      let city = c.city || '';
      if (!npa && !city && street.includes(',')) {
        const parts = street.split(',');
        street = parts[0]?.trim() || '';
        const cityPart = (parts.slice(1).join(',') || '').trim();
        const npaMatch = cityPart.match(/^(\d{4})\s*(.*)$/);
        npa = npaMatch?.[1] || '';
        city = npaMatch?.[2] || cityPart;
      }
      setForm({
        client_type: c.client_type || 'particulier',
        company_name: c.company_name || '',
        contact_first_name: c.contact_first_name || '',
        contact_last_name: c.contact_last_name || '',
        contact_phone: c.contact_phone || '',
        contact_email: c.contact_email || '',
        first_name: c.first_name || '', last_name: c.last_name || '',
        phone: c.phone || '', email: c.email || '',
        address: street || '', npa: npa || '', city: city || '',
        salutation: c.salutation || '',
        birthday: c.birthday || '',
        notification_preference: c.notification_preference || 'whatsapp',
        notes: c.notes || '',
        status: c.status || 'prospect',
        current_vehicle_brand: c.current_vehicle_brand || '',
        current_vehicle_model: c.current_vehicle_model || '',
        accepts_offers: c.accepts_offers || false,
        offer_channel: c.offer_channel || 'none',
        next_follow_up_date: c.next_follow_up_date || '',
        source: c.source || '',
        was_previous_client: c.was_previous_client || false,
        do_not_contact: c.do_not_contact || false,
        no_longer_owns_vehicle: c.no_longer_owns_vehicle || false,
      });
    } catch { /* fallback to list data if single fetch fails */ }
  };

  const openNew = () => {
    setEditClient(null);
    setForm(emptyForm);
    setVForm({ brand: '', model: '', type: '', plate: '', mileage: 0, vin: '' });
    setStep('client');
    setShowDialog(true);
  };

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-8 space-y-6">
        {fromAgenda && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between" data-testid="from-agenda-banner">
            <p className="text-sm text-blue-700 font-medium">Creez un client pour votre rendez-vous. Vous serez redirige vers l'agenda automatiquement.</p>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/agenda')} className="text-blue-600">Retour a l'agenda</Button>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-heading font-bold text-slate-900">Clients</h1>
            <p className="text-base text-slate-600 mt-1">{stats ? `${stats.total} client(s)` : '...'}</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedClients.size > 0 && isAdmin && (
              <Button
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="bulk-delete-btn"
              >
                <Trash2 className="mr-2 w-4 h-4" />
                Supprimer ({selectedClients.size})
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => handleExport(false)}
              data-testid="export-clients-btn"
            >
              <Download className="mr-2 w-4 h-4" />
              Exporter Excel
            </Button>
            <input
              ref={importFileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleImport}
              data-testid="import-file-input"
            />
            <input
              ref={replaceFileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleReplaceAll}
              data-testid="replace-file-input"
            />
            <Button
              variant="outline"
              onClick={() => importFileRef.current?.click()}
              disabled={importing}
              data-testid="import-clients-btn"
            >
              <Upload className="mr-2 w-4 h-4" />
              {importing ? 'Import en cours...' : 'Importer Excel'}
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={() => setShowReplaceConfirm(true)}
                disabled={replacing}
                data-testid="replace-all-clients-btn"
              >
                <RefreshCw className="mr-2 w-4 h-4" />
                {replacing ? 'Remplacement...' : 'Remplacer tout'}
              </Button>
            )}
            <Button onClick={openNew} className="btn-primary" data-testid="new-client-btn">
              <Plus className="mr-2 w-4 h-4" /> {t('clients.new_client')}
            </Button>
          </div>
        </div>

        {/* Search + Filter */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher par nom, telephone, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="client-search"
            />
          </div>
          {isAdmin && (
            <Button
              variant={showInactive ? "default" : "outline"}
              size="sm"
              onClick={() => setShowInactive(!showInactive)}
              data-testid="toggle-inactive-clients"
            >
              {showInactive ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
              {showInactive ? 'Tous' : 'Voir inactifs'}
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap" data-testid="client-filters">
          {isAdmin && filteredClients.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full transition-colors bg-slate-100 text-slate-600 hover:bg-slate-200"
              data-testid="select-all-clients"
            >
              {selectedClients.size === filteredClients.length && filteredClients.length > 0 ? (
                <CheckSquare className="w-3.5 h-3.5 text-slate-900" />
              ) : (
                <Square className="w-3.5 h-3.5" />
              )}
              Tout selectionner
            </button>
          )}
          {selectedClients.size > 0 && (
            <span className="text-xs font-semibold text-slate-900 bg-slate-200 px-2 py-1 rounded-full" data-testid="selected-count">
              {selectedClients.size} selectionne(s)
            </span>
          )}
          {stats && (
            <>
              <span className="text-xs text-slate-500 font-medium">Filtres:</span>
              {[
                { key: 'all', label: 'Tous', count: stats.total },
                { key: 'particulier', label: 'Particuliers', count: stats.particuliers },
                { key: 'entreprise', label: 'Entreprises', count: stats.entreprises },
              ].map(f => (
                <button key={f.key} onClick={() => { setFilterType(f.key); setSelectedClients(new Set()); }}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${filterType === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`filter-type-${f.key}`}
                >{f.label} ({f.count})</button>
              ))}
              <span className="text-slate-300 mx-1">|</span>
              {[
                { key: 'all', label: 'Tous', count: stats.total },
                { key: 'madame', label: 'Madame', count: stats.madame },
                { key: 'monsieur', label: 'Monsieur', count: stats.monsieur },
              ].map(f => (
                <button key={f.key} onClick={() => { setFilterCivil(f.key); setSelectedClients(new Set()); }}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${filterCivil === f.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`filter-civil-${f.key}`}
                >{f.label} ({f.count})</button>
              ))}
            </>
          )}
        </div>

        {/* Client List */}
        {loading ? (
          <p className="text-center text-slate-500 py-8">{t('common.loading')}</p>
        ) : !search && filterType === 'all' && filterCivil === 'all' ? (
          <div className="text-center py-16" data-testid="search-prompt">
            <Search className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-lg font-medium text-slate-500">Tapez un nom pour rechercher un client</p>
            <p className="text-sm text-slate-400 mt-1">ou utilisez les filtres ci-dessus</p>
          </div>
        ) : filteredClients.length === 0 ? (
          <Card className="p-12 text-center rounded-xl">
            <User className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">{t('common.no_results')}</p>
          </Card>
        ) : (
          <>
          <p className="text-xs text-slate-400 mb-1">{filteredClients.length} resultat(s)</p>
          <div className="grid gap-3">
            {filteredClients.map((client) => (
              <Card
                key={client.id}
                className={`p-4 rounded-xl border shadow-sm hover:shadow-md transition-shadow cursor-pointer ${
                  selectedClients.has(client.id) ? 'border-blue-400 bg-blue-50/40 ring-1 ring-blue-300' :
                  client.active === false ? 'border-orange-200 bg-orange-50/30 opacity-70' : 'border-slate-200'
                }`}
                onClick={() => navigate(`/admin/clients/${client.id}`)}
                data-testid={`client-card-${client.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {isAdmin && (
                      <button
                        onClick={(e) => toggleSelectClient(client.id, e)}
                        className="shrink-0 p-0.5 rounded hover:bg-slate-100 transition-colors"
                        data-testid={`select-client-${client.id}`}
                      >
                        {selectedClients.has(client.id) ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-300 hover:text-slate-500" />
                        )}
                      </button>
                    )}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${client.active === false ? 'bg-orange-300 text-white' : 'bg-slate-900 text-white'}`}>
                      {client.first_name?.[0]}{client.last_name?.[0]}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        {client.active === false && <span className="text-xs bg-orange-100 text-orange-600 px-1.5 py-0.5 rounded font-medium mr-1">Inactif</span>}
                        {client.client_type === 'entreprise' && client.company_name
                          ? <>{client.company_name} <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium ml-1">Entreprise</span></>
                          : <>{client.first_name} {client.last_name}</>
                        }
                        <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                          client.status === 'client' ? 'bg-emerald-100 text-emerald-700' :
                          client.status === 'prospect' ? 'bg-blue-100 text-blue-700' :
                          client.status === 'ancien_client' ? 'bg-slate-100 text-slate-600' :
                          'bg-blue-100 text-blue-700'
                        }`}>{
                          client.status === 'client' ? 'Client' :
                          client.status === 'ancien_client' ? 'Ancien' : 'Prospect'
                        }</span>
                      </p>
                      {client.client_type === 'entreprise' && (client.contact_first_name || client.contact_last_name) && (
                        <p className="text-xs text-blue-600 mt-0.5">Contact : {client.contact_first_name} {client.contact_last_name}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        {client.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {client.phone}</span>}
                        {client.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {client.email}</span>}
                        {client.current_vehicle_brand && (
                          <span className="text-slate-400">{client.current_vehicle_brand} {client.current_vehicle_model}</span>
                        )}
                        {client.next_follow_up_date && (
                          <span className={`font-medium ${new Date(client.next_follow_up_date) <= new Date() ? 'text-red-500' : 'text-amber-500'}`}>
                            Relance: {client.next_follow_up_date}
                          </span>
                        )}
                        {client.do_not_contact && (
                          <span className="text-red-500 font-semibold">Ne pas contacter</span>
                        )}
                        {client.no_longer_owns_vehicle && (
                          <span className="text-amber-500 font-semibold">Plus de vehicule</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className={client.active === false ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50' : 'text-orange-500 hover:text-orange-700 hover:bg-orange-50'}
                        onClick={(e) => toggleClientActive(e, client.id)}
                        data-testid={`toggle-active-${client.id}`}
                      >
                        {client.active === false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-amber-500 hover:text-amber-700 hover:bg-amber-50"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            await axios.post(`${API}/deletion-requests`, {
                              type: 'deactivate_client',
                              target_id: client.id,
                              target_label: `${client.first_name} ${client.last_name}`
                            });
                            toast.success('Demande de désactivation envoyée à l\'administrateur');
                          } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
                        }}
                        data-testid={`request-deactivate-${client.id}`}
                        title="Demander la désactivation"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/admin/clients/${client.id}`); }} data-testid={`edit-client-${client.id}`}>
                      Voir
                    </Button>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
          </>
        )}

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-700">
                <AlertTriangle className="w-5 h-5" />
                Confirmer la suppression
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Vous etes sur le point de supprimer definitivement <strong className="text-red-600">{selectedClients.size} client(s)</strong>.
              </p>
              <p className="text-xs text-slate-500">
                Cette action supprimera egalement les vehicules, interactions CRM et gardiennages associes. Cette action est irreversible.
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} data-testid="cancel-bulk-delete">{t('common.cancel')}</Button>
                <Button variant="destructive" onClick={handleBulkDelete} disabled={deleting} data-testid="confirm-bulk-delete">
                  <Trash2 className="w-4 h-4 mr-1" />
                  {deleting ? 'Suppression...' : `Supprimer ${selectedClients.size} client(s)`}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Replace All Confirmation Dialog */}
        <Dialog open={showReplaceConfirm} onOpenChange={setShowReplaceConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-amber-700">
                <RefreshCw className="w-5 h-5" />
                Remplacer tous les clients
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Cette action va <strong className="text-red-600">supprimer definitivement tous les {stats?.total || 0} clients</strong> ainsi que leurs vehicules, interactions et gardiennages.
              </p>
              <p className="text-sm text-slate-600">
                Ensuite, le fichier Excel que vous choisirez sera importe comme nouvelle base de donnees clients.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800 font-medium">
                  Conseil : Exportez d'abord vos clients actuels avant de les remplacer.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setShowReplaceConfirm(false)} data-testid="cancel-replace-all">{t('common.cancel')}</Button>
                <Button
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => replaceFileRef.current?.click()}
                  disabled={replacing}
                  data-testid="confirm-replace-all"
                >
                  <Upload className="w-4 h-4 mr-1" />
                  {replacing ? 'Remplacement en cours...' : 'Choisir le fichier et remplacer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={(open) => { if (!open) { setStep('client'); setNewClientId(null); } setShowDialog(open); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {step === 'client' ? (
              <>
                <DialogHeader><DialogTitle>{editClient ? t('clients.edit_client') : t('clients.new_client')}</DialogTitle></DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Client Type Selector */}
                  <div className="flex gap-2 p-1 bg-slate-100 rounded-lg w-fit" data-testid="client-type-selector">
                    <button type="button"
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${form.client_type === 'particulier' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                      onClick={() => setForm({ ...form, client_type: 'particulier' })}
                      data-testid="client-type-particulier"
                    >Particulier</button>
                    <button type="button"
                      className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${form.client_type === 'entreprise' ? 'bg-white shadow text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}
                      onClick={() => setForm({ ...form, client_type: 'entreprise' })}
                      data-testid="client-type-entreprise"
                    >Entreprise</button>
                  </div>

                  {/* Enterprise fields */}
                  {form.client_type === 'entreprise' && (
                    <div className="bg-blue-50 p-4 rounded-lg space-y-3 border border-blue-100">
                      <p className="text-xs font-bold text-blue-700 uppercase tracking-wider">Informations entreprise</p>
                      <div className="space-y-1">
                        <Label className="text-xs">Raison sociale *</Label>
                        <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="SA, Sarl..." required data-testid="client-company-name" />
                      </div>
                      <p className="text-xs font-semibold text-blue-600 mt-2">Personne de contact</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Prenom contact</Label>
                          <Input value={form.contact_first_name} onChange={(e) => setForm({ ...form, contact_first_name: e.target.value })} data-testid="client-contact-firstname" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Nom contact</Label>
                          <Input value={form.contact_last_name} onChange={(e) => setForm({ ...form, contact_last_name: e.target.value })} data-testid="client-contact-lastname" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Tel. contact</Label>
                          <Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="client-contact-phone" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email contact</Label>
                          <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="client-contact-email" />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {/* LEFT COLUMN - Coordonnées */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-1 border-b border-slate-200">
                        <User className="w-4 h-4 text-slate-500" />
                        <p className="text-sm font-semibold text-slate-700">{form.client_type === 'entreprise' ? 'Adresse de l\'entreprise' : 'Coordonnees'}</p>
                      </div>
                      {form.client_type !== 'entreprise' && (
                        <>
                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-1">
                              <Label className="text-xs">Civilite</Label>
                              <Select value={form.salutation} onValueChange={(v) => setForm({ ...form, salutation: v })}>
                                <SelectTrigger data-testid="client-salutation"><SelectValue placeholder="--" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="monsieur">Monsieur</SelectItem>
                                  <SelectItem value="madame">Madame</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Prenom</Label>
                              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="client-firstname" />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-xs">Nom</Label>
                              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="client-lastname" />
                            </div>
                          </div>
                        </>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Telephone</Label>
                          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-phone" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Email</Label>
                          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" />
                        </div>
                      </div>
                      {form.client_type !== 'entreprise' && (
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Date de naissance</Label>
                            <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="client-birthday" />
                          </div>
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Rue</Label>
                        <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Route de Neuchatel 13" data-testid="client-address" />
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">NPA</Label>
                          <Input value={form.npa} onChange={(e) => setForm({ ...form, npa: e.target.value })} placeholder="2520" maxLength={4} data-testid="client-npa" />
                        </div>
                        <div className="space-y-1 col-span-2">
                          <Label className="text-xs">Ville</Label>
                          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="La Neuveville" data-testid="client-city" />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Notes internes</Label>
                        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="client-notes" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> Preference de notification</Label>
                        <Select value={form.notification_preference} onValueChange={(v) => setForm({ ...form, notification_preference: v })}>
                          <SelectTrigger data-testid="client-notification-pref"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="whatsapp">WhatsApp</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="both">WhatsApp + Email</SelectItem>
                            <SelectItem value="phone">Telephone uniquement</SelectItem>
                            <SelectItem value="none">Aucune</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* RIGHT COLUMN - CRM */}
                    <div className="space-y-4">
                      <div className="flex items-center gap-2 pb-1 border-b border-slate-200">
                        <Car className="w-4 h-4 text-slate-500" />
                        <p className="text-sm font-semibold text-slate-700">Informations CRM</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">{t('common.status')}</Label>
                          <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                            <SelectTrigger data-testid="client-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="prospect">Prospect</SelectItem>
                              <SelectItem value="client">Client</SelectItem>
                              <SelectItem value="ancien_client">Ancien client</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Source</Label>
                          <Select value={form.source || 'walk_in'} onValueChange={v => setForm({...form, source: v})}>
                            <SelectTrigger data-testid="client-source"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="walk_in">Passage au garage</SelectItem>
                              <SelectItem value="phone">Telephone</SelectItem>
                              <SelectItem value="web">Internet</SelectItem>
                              <SelectItem value="referral">Recommandation</SelectItem>
                              <SelectItem value="import">Import</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Date de relance</Label>
                          <Input type="date" value={form.next_follow_up_date} onChange={e => setForm({...form, next_follow_up_date: e.target.value})}
                            data-testid="client-followup-date" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Accepte offres/news</Label>
                          <Select value={form.accepts_offers ? form.offer_channel || 'email' : 'none'}
                            onValueChange={v => setForm({...form, accepts_offers: v !== 'none', offer_channel: v})}>
                            <SelectTrigger data-testid="client-offers"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Non</SelectItem>
                              <SelectItem value="email">Oui - Email</SelectItem>
                              <SelectItem value="whatsapp">Oui - WhatsApp</SelectItem>
                              <SelectItem value="both">Oui - Email + WhatsApp</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2.5">
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Statut client</p>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={form.was_previous_client} onChange={e => setForm({...form, was_previous_client: e.target.checked})}
                            className="rounded" data-testid="client-was-previous" />
                          <Label className="text-xs cursor-pointer">Etait deja client chez nous</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={form.do_not_contact} onChange={e => setForm({...form, do_not_contact: e.target.checked})}
                            className="rounded border-red-300 text-red-600 focus:ring-red-500" data-testid="client-do-not-contact" />
                          <Label className="text-xs cursor-pointer text-red-600">Ne souhaite plus etre contacte</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="checkbox" checked={form.no_longer_owns_vehicle} onChange={e => setForm({...form, no_longer_owns_vehicle: e.target.checked})}
                            className="rounded border-amber-300 text-amber-600 focus:ring-amber-500" data-testid="client-no-vehicle" />
                          <Label className="text-xs cursor-pointer text-amber-600">Ne possede plus de vehicule</Label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full btn-primary" data-testid="submit-client-btn">
                    {editClient ? 'Enregistrer' : 'Suivant - Ajouter un véhicule'}
                  </Button>
                </form>
              </>
            ) : (
              <>
                <DialogHeader><DialogTitle>Ajouter un véhicule</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500 -mt-2">Client: {form.first_name} {form.last_name}</p>
                <form onSubmit={handleVehicleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Marque</Label>
                      <Input value={vForm.brand} onChange={(e) => setVForm({ ...vForm, brand: e.target.value })} placeholder="BMW" required data-testid="v-brand" />
                    </div>
                    <div className="space-y-2">
                      <Label>Modele</Label>
                      <Input value={vForm.model} onChange={(e) => setVForm({ ...vForm, model: e.target.value })} placeholder="320d" data-testid="v-model" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Plaque</Label>
                      <Input value={vForm.plate} onChange={(e) => setVForm({ ...vForm, plate: e.target.value.toUpperCase() })} placeholder="BE 12345" required data-testid="v-plate" />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Input value={vForm.type} onChange={(e) => setVForm({ ...vForm, type: e.target.value })} placeholder="Berline" data-testid="v-type" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Kilometrage</Label>
                      <Input type="number" value={vForm.mileage} onChange={(e) => setVForm({ ...vForm, mileage: parseInt(e.target.value) || 0 })} data-testid="v-mileage" />
                    </div>
                    <div className="space-y-2">
                      <Label>VIN (optionnel)</Label>
                      <Input value={vForm.vin} onChange={(e) => setVForm({ ...vForm, vin: e.target.value.toUpperCase() })} data-testid="v-vin" />
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button type="button" variant="outline" className="flex-1" onClick={skipVehicle} data-testid="skip-vehicle-btn">
                      Passer
                    </Button>
                    <Button type="submit" className="flex-1 btn-primary" data-testid="submit-vehicle-btn">
                      Ajouter le véhicule
                    </Button>
                  </div>
                </form>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default ClientsPage;
